import { inventoryMediaTypesToServiceInventoryMediaTypes, InventoryMediaType, inventoryVideoTypesToServiceInventoryVideoTypes } from "../../../../models/inventories";
import { parsePagingQuery, toQueryParam } from "../../../../handlers/filter";
import { DealArchiveStatus, DealGroupType, DealGroupTypeToServiceDealGroupType, DealPriceType, DealStatus, DealType, DealSourceType, FilterTypes, ServiceDealPriceType, ServiceDealSourceType, ServiceDealType, dealStatusToServiceStatus } from "../../../../types";
import { formatDate } from "../../../../utils";
import { sortToQueryParam } from "./sort";
export const DEFAULT_DEAL_TYPES_FILTER = [
    ServiceDealType.PMP,
    ServiceDealType.PG,
    ServiceDealType.SUPER_TAG
];
const statusParamValue = (incomingStatuses, archiveStatus) => {
    if (!incomingStatuses?.length &&
        (!archiveStatus?.length || archiveStatus === DealArchiveStatus.BOTH)) {
        return "";
    }
    const nonArchivedStatuses = (incomingStatuses && !Array.isArray(incomingStatuses)
        ? [incomingStatuses]
        : incomingStatuses || []).filter((status) => status && status !== DealStatus.ARCHIVED);
    const statuses = [];
    if (archiveStatus === DealArchiveStatus.ARCHIVED) {
        statuses.push(DealStatus.ARCHIVED);
    }
    else {
        statuses.push(...(nonArchivedStatuses?.length
            ? nonArchivedStatuses
            : [
                DealStatus.READY,
                DealStatus.TEST,
                DealStatus.CANCELLED,
                DealStatus.DRAFT,
                DealStatus.PAUSED
            ]));
        if (archiveStatus === DealArchiveStatus.BOTH && !statuses.includes(DealStatus.ARCHIVED)) {
            statuses.push(DealStatus.ARCHIVED);
        }
    }
    if (statuses.length) {
        return toQueryParam({
            field: "status",
            type: FilterTypes.IN,
            value: statuses.map(dealStatusToServiceStatus)
        });
    }
    return "";
};
/**
 * @param wheres: the `where` portion in an SDK filter object.
 * @return: a properly-formatted query parameter string, based on the filters. If there are none, the empty string.
 */
const wheresToQueryParams = (wheres) => {
    let params = "";
    const whereMap = new Map(wheres?.map((filter) => [filter.field, filter]) || []);
    // ensuring the following filters are present while looping through map.
    if (!whereMap.has("id") && !whereMap.has("dealTypes")) {
        whereMap.set("dealTypes", { value: [], type: FilterTypes.IN, field: "dealTypes" });
    }
    if (!whereMap.has("status")) {
        whereMap.set("status", { value: [], type: FilterTypes.IN, field: "status" });
    }
    if (wheres) {
        whereMap.forEach((filter, field) => {
            const valueArr = Array.isArray(filter.value) ? filter.value : [filter.value];
            switch (field) {
                case "search": {
                    const value = toQueryParam(filter);
                    if (value) {
                        params = `${params}&search=${value}`;
                    }
                    break;
                }
                case "typeFilter": {
                    const value = valueArr[0];
                    if (value) {
                        params = `${params}&typeFilter=${DealGroupTypeToServiceDealGroupType[value]}`;
                    }
                    break;
                }
                case "status": {
                    const archiveStatus = whereMap.get("archiveStatus")?.value;
                    const value = statusParamValue(valueArr.filter((status) => !!status), Array.isArray(archiveStatus) ? archiveStatus[0] : archiveStatus);
                    if (value) {
                        params = `${params}&statuses=${value}`;
                    }
                    break;
                }
                case "id": {
                    const value = valueArr.filter((id) => !!id);
                    if (value.length) {
                        params = `${params}&ids=${toQueryParam({
                            ...filter,
                            value
                        }, { array: true })}`;
                    }
                    break;
                }
                case "parentId": {
                    const value = valueArr.filter((id) => !!id);
                    if (value.length) {
                        params = `${params}&parentId=${toQueryParam({
                            ...filter,
                            value
                        }, { array: false })}`;
                    }
                    break;
                }
                case "dealTypes": {
                    const value = [];
                    for (const dealType of valueArr) {
                        switch (dealType) {
                            case DealType.SUPER_TAG:
                                value.push(ServiceDealType.SUPER_TAG);
                                break;
                            case DealType.PMP:
                                value.push(ServiceDealType.PMP);
                                break;
                            case DealType.PG:
                                value.push(ServiceDealType.PG);
                                break;
                            case DealType.OPEN_MARKET:
                                value.push(ServiceDealType.OPEN_MARKET);
                                break;
                            case DealType.DEAL_GROUP:
                                value.push(ServiceDealType.DEAL_GROUP);
                                break;
                        }
                    }
                    if (!value.length) {
                        // don't add default dealTypes to deal groups
                        if (whereMap.has("typeFilter") &&
                            whereMap.get("typeFilter")?.value === DealGroupType.GROUP) {
                            break;
                        }
                        value.push(...DEFAULT_DEAL_TYPES_FILTER);
                    }
                    params = `${params}&dealTypes=${toQueryParam({
                        ...filter,
                        value
                    }, { array: true })}`;
                    break;
                }
                case "priceTypes": {
                    const value = [];
                    for (const priceType of valueArr) {
                        switch (priceType) {
                            case DealPriceType.FIXED:
                                value.push(ServiceDealPriceType.FIXED, ServiceDealPriceType.PG);
                                break;
                            case DealPriceType.FLOOR:
                                value.push(ServiceDealPriceType.FLOOR);
                                break;
                        }
                    }
                    if (value.length) {
                        params = `${params}&costFuncs=${toQueryParam({
                            ...filter,
                            value
                        }, { array: true })}`;
                    }
                    break;
                }
                case "mediaTypes": {
                    const value = inventoryMediaTypesToServiceInventoryMediaTypes(valueArr);
                    if (value.length) {
                        params = `${params}&mediaTypes=${toQueryParam({
                            ...filter,
                            value
                        }, { array: true })}`;
                    }
                    break;
                }
                case "videoTypes": {
                    const value = inventoryVideoTypesToServiceInventoryVideoTypes(valueArr, [
                        InventoryMediaType.VIDEO
                    ]);
                    if (value.length) {
                        params = `${params}&videoTypes=${toQueryParam({
                            ...filter,
                            value
                        }, { array: true })}`;
                    }
                    break;
                }
                case "minDate": {
                    const value = whereMap.get("minDate")?.value?.[0];
                    const formattedValue = value instanceof Date && value.getTime() ? formatDate(value) : undefined;
                    if (formattedValue !== undefined) {
                        params = `${params}&minDate=${formattedValue}`;
                    }
                    break;
                }
                case "maxDate": {
                    const value = whereMap.get("maxDate")?.value?.[0];
                    const formattedValue = value instanceof Date && value.getTime() ? formatDate(value) : undefined;
                    if (formattedValue !== undefined) {
                        params = `${params}&maxDate=${formattedValue}`;
                    }
                    break;
                }
                case "source": {
                    const value = [];
                    for (const sourceType of valueArr) {
                        switch (sourceType) {
                            case DealSourceType.SELF_SOURCED:
                                value.push(ServiceDealSourceType.SELF_SOURCED);
                                break;
                            case DealSourceType.MADHIVE_PRIVATE:
                                value.push(ServiceDealSourceType.MADHIVE_PRIVATE);
                                break;
                            case DealSourceType.MADHIVE_PUBLIC:
                                value.push(ServiceDealSourceType.MADHIVE_PUBLIC);
                                break;
                            case DealSourceType.PRIVATE_O_AND_O:
                                value.push(ServiceDealSourceType.PRIVATE_O_AND_O);
                                break;
                        }
                    }
                    const uniqueValue = [...new Set(value)];
                    if (uniqueValue.length) {
                        params = `${params}&source=${toQueryParam({
                            ...filter,
                            value: uniqueValue
                        }, { array: false })}`;
                    }
                    break;
                }
                case "readAndPurchaseOnly": {
                    if (valueArr.length) {
                        params = `${params}&readAndPurchaseOnly=${toQueryParam({
                            ...filter,
                            value: valueArr[0]
                        }, { array: false })}`;
                    }
                    break;
                }
            }
        });
    }
    return params;
};
/**
 * @param filters: array of SDK-style deals filters.
 * @param sort: optional. Information on the field and direction of sorting, if any.
 * @return: a proper query string for the supplied filters.
 */
export const toQueryParams = (filters, sort) => {
    const params = `${wheresToQueryParams(filters.where)}${parsePagingQuery(filters.paging)}${sortToQueryParam(sort)}`;
    return params.length ? `?${params}` : "";
};
//# sourceMappingURL=params.js.map